import BrowserFeatures, { FeatureDivider } from 'components/features-page/BrowserFeatures'
import { ReactElement, ReactNode, useState } from 'react'
import { Box, Button, Flex, ThemeUIStyleObject, Text } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'
import FlexibleDNSIcon from 'images/flexible-dns.svg'
import { Feature, FeatureWrapper } from 'components/features-page/Features'
import MacSpoofingIcon from 'images/mac-spoofing.svg'
import TCPSocketTerminationIcon from 'images/tcp-socket-termination.svg'
import ProxyGatewatIcon from 'images/proxy-gateway.svg'
import ProxySettingsIcon from 'images/proxy-settings.svg'
import SecureHotspotIcon from 'images/secure-hotspot.svg'
import DoubleHopIcon from 'images/double-hop.svg'
import FirewallIcon from 'images/firewall-icon.svg'
import UpdateChannels from 'images/update-channels.svg'
import BlockerFeaturesIcon from 'images/blocker-features.svg'
import CookieMonsterIcon from 'images/cookie-monster.svg'
import AdPrivacyIcon from 'images/ad-privacy.svg'
import TimeWarpIcon from 'images/time-warp.svg'
import LocationWarpIcon from 'images/location-warp.svg'
import SplitPersonalityIcon from 'images/split-personality.svg'
import WebRTCIcon from 'images/webrtc-slayer.svg'
import DoNotDisturbIcon from 'images/do-not-disturb.svg'
import LanguageWarpIcon from 'images/language-warp.svg'
import AutoPilotIcon from 'images/auto-pilot.svg'
import WorkerBlockIcon from 'images/worker-block.svg'
import GPSSpoofingIcon from 'images/gps-spoofing.svg'
import DecoyTrafficIcon from 'images/decoy-traffic.svg'
import Svg from 'components/UI/SVG'
import DesktopIcon from 'images/desktop-features.svg'
import BrowserIcon from 'images/browser-extension.svg'
import MobileIcon from 'images/mobile-extension.svg'
import TVIcon from 'images/tv-app-icon.svg'
import AntiFingerprintingIcon from 'images/anti-fingerprinting-icon.svg'

export interface FeatureSelectorList {
  [key: string]: FeatureSelectorListItem[]
}

export interface FeatureSelectorListItem {
  title?: string | null
  description?: string | null
  icon: React.ElementType
  href?: string
}

const buttonStyles: ThemeUIStyleObject = {
  display: 'flex',
  gap: '1.6rem',
  backgroundColor: 'transparent',
  position: 'relative',
  alignItems: 'center',
  fontSize: '1.8rem',
  fontWeight: 'bold',
}

export type FeatureSet = 'desktop' | 'browser' | 'mobile' | 'tv'

export default function FeatureSelector({
  data,
}: {
  data: Queries.KnowledgeBaseFeaturesPageQuery
}): ReactElement {
  const [selectedFeatureSet, setSelectedFeatureSet] = useState<FeatureSet>('desktop')
  const featuresPageStrings = getEnglishStrings(data.directus.features_page?.strings)
  const generalTranslations = getEnglishStrings(data.directus.general_translations?.strings)

  const featureList: FeatureSelectorList = {
    desktop: [
      {
        title: featuresPageStrings?.flexible_dns,
        description: featuresPageStrings?.flexible_dns_subtitle,
        icon: FlexibleDNSIcon,
      },
      {
        title: featuresPageStrings?.mac_spoofing,
        description: featuresPageStrings?.mac_spoofing_subtitle,
        icon: MacSpoofingIcon,
      },
      {
        title: featuresPageStrings?.tcp_socket_termination,
        description: featuresPageStrings?.tcp_socket_termination_subtitle,
        icon: TCPSocketTerminationIcon,
      },
      {
        title: featuresPageStrings?.proxy_gateway,
        description: featuresPageStrings?.proxy_gateway_subtitle,
        icon: ProxyGatewatIcon,
      },
      {
        title: featuresPageStrings?.proxy_settings,
        description: featuresPageStrings?.proxy_settings_subtitle,
        icon: ProxySettingsIcon,
      },
      {
        title: featuresPageStrings?.secure_hotspot,
        description: featuresPageStrings?.secure_hotspot_subtitle,
        icon: SecureHotspotIcon,
      },
      {
        title: featuresPageStrings?.double_hop,
        description: featuresPageStrings?.double_hop_subtitle,
        icon: DoubleHopIcon,
      },
      {
        title: featuresPageStrings?.firewall,
        description: featuresPageStrings?.firewall_subtitle,
        icon: FirewallIcon,
      },
      {
        title: featuresPageStrings?.update_channels,
        description: featuresPageStrings?.update_channels_subtitle,
        icon: UpdateChannels,
      },
      {
        title: featuresPageStrings?.auto_pilot,
        description: featuresPageStrings?.auto_pilot_subtitle,
        icon: AutoPilotIcon,
        href: '/features/cruise-control',
      },
    ],
    browser: [
      {
        title: featuresPageStrings?.blocker_features,
        description: featuresPageStrings?.blocker_features_subtitle,
        icon: BlockerFeaturesIcon,
        href: '/features/ad-blocking',
      },
      {
        title: featuresPageStrings?.cookie_monster,
        description: featuresPageStrings?.cookie_monster_subtitle,
        icon: CookieMonsterIcon,
      },
      {
        title: featuresPageStrings?.ad_privacy,
        description: featuresPageStrings?.ad_privacy_subtitle,
        icon: AdPrivacyIcon,
      },
      {
        title: featuresPageStrings?.time_warp,
        description: featuresPageStrings?.time_warp_subtitle,
        icon: TimeWarpIcon,
        href: '/features/timezone-spoofing',
      },
      {
        title: featuresPageStrings?.location_warp,
        description: featuresPageStrings?.location_warp_subtitle,
        icon: LocationWarpIcon,
      },
      {
        title: featuresPageStrings?.split_personality,
        description: featuresPageStrings?.split_personality_subtitle,
        icon: SplitPersonalityIcon,
      },
      {
        title: featuresPageStrings?.double_hop,
        description: featuresPageStrings?.double_hop_subtitle,
        icon: DoubleHopIcon,
      },
      {
        title: featuresPageStrings?.webrtc_slayer,
        description: featuresPageStrings?.webrtc_slayer_subtitle,
        icon: WebRTCIcon,
        href: '/features/webrtc-slayer',
      },
      {
        title: featuresPageStrings?.do_not_disturb,
        description: featuresPageStrings?.do_not_disturb_subtitle,
        icon: DoNotDisturbIcon,
        href: '/features/dnd',
      },
      {
        title: featuresPageStrings?.language_warp,
        description: featuresPageStrings?.language_warp_subtitle,
        icon: LanguageWarpIcon,
        href: '/features/languagewarp',
      },
      {
        title: featuresPageStrings?.auto_pilot,
        description: featuresPageStrings?.auto_pilot_subtitle,
        icon: AutoPilotIcon,
        href: '/features/cruise-control',
      },
      {
        title: featuresPageStrings?.worker_block,
        description: featuresPageStrings?.worker_block_subtitle,
        icon: WorkerBlockIcon,
        href: '/features/worker-block',
      },
      {
        title: featuresPageStrings?.anti_fingerprinting_title,
        description: featuresPageStrings?.anti_fingerprinting_subtitle,
        icon: AntiFingerprintingIcon,
        href: '/features/anti-fingerprinting',
      },
    ],
    mobile: [
      {
        title: featuresPageStrings?.gps_spoofing,
        description: featuresPageStrings?.gps_spoofing_subtitle,
        icon: GPSSpoofingIcon,
      },
      {
        title: featuresPageStrings?.decoy_traffic,
        description: featuresPageStrings?.decoy_traffic_subtitle,
        icon: DecoyTrafficIcon,
      },
      {
        title: featuresPageStrings?.flexible_dns,
        description: featuresPageStrings?.flexible_dns_subtitle,
        icon: FlexibleDNSIcon,
      },
      {
        title: featuresPageStrings?.auto_pilot,
        description: featuresPageStrings?.auto_pilot_subtitle,
        icon: AutoPilotIcon,
        href: '/features/cruise-control',
      },
    ],
    tv: [
      {
        title: featuresPageStrings?.tcp_socket_termination,
        description: featuresPageStrings?.tcp_socket_termination_subtitle,
        icon: TCPSocketTerminationIcon,
      },
      {
        title: featuresPageStrings?.gps_spoofing,
        description: featuresPageStrings?.gps_spoofing_subtitle,
        icon: GPSSpoofingIcon,
      },
      {
        title: featuresPageStrings?.auto_pilot,
        description: featuresPageStrings?.auto_pilot_subtitle,
        icon: AutoPilotIcon,
        href: '/features/cruise-control',
      },
      {
        title: featuresPageStrings?.decoy_traffic,
        description: featuresPageStrings?.decoy_traffic_subtitle,
        icon: DecoyTrafficIcon,
      },
    ],
  }

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        gap: '4.8rem',
      }}
    >
      <FeatureChangerWrapper>
        <FeatureChanger
          onClick={() => setSelectedFeatureSet('desktop')}
          icon={DesktopIcon}
          title={generalTranslations?.desktop}
          active={selectedFeatureSet === 'desktop'}
        />
        <FeatureChanger
          onClick={() => setSelectedFeatureSet('mobile')}
          icon={MobileIcon}
          title={generalTranslations?.mobile}
          active={selectedFeatureSet === 'mobile'}
        />
        <FeatureChanger
          onClick={() => setSelectedFeatureSet('browser')}
          icon={BrowserIcon}
          title={generalTranslations?.browser}
          active={selectedFeatureSet === 'browser'}
        />
        <FeatureChanger
          onClick={() => setSelectedFeatureSet('tv')}
          icon={TVIcon}
          title={generalTranslations?.tv}
          active={selectedFeatureSet === 'tv'}
        />
      </FeatureChangerWrapper>

      <FeatureWrapper>
        {featureList[selectedFeatureSet].map((feature, index) => (
          <Feature
            key={index}
            title={feature.title}
            description={feature.description}
            icon={feature.icon}
            href={feature.href}
          />
        ))}
      </FeatureWrapper>
      <BrowserFeatures
        browserExtensionFeaturesText={featuresPageStrings?.browser_extension_features}
        desktopAppFeaturesText={featuresPageStrings?.desktop_app_features}
        mobileAppFeaturesText={featuresPageStrings?.mobile_features}
        tvAppFeaturesText={featuresPageStrings?.tv_app_features}
        activeGroup={selectedFeatureSet}
      />
    </Flex>
  )
}

export function FeatureListItemUnderline(): ReactElement {
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: '-2.1rem',
        left: 0,
        width: '100%',
        height: '0.2rem',
        backgroundColor: 'white',
      }}
    />
  )
}

export function FeatureChanger({
  onClick,
  icon,
  title,
  active,
  dataTestId,
}: {
  onClick: () => void
  icon?: React.ElementType
  title: string | null | undefined
  active: boolean
  dataTestId?: string
}): ReactElement {
  return (
    <Button
      data-testid={dataTestId}
      onClick={onClick}
      sx={{
        ...buttonStyles,
        svg: {
          path: {
            fill: active ? 'white' : 'white50',
          },
          'path,circle,rect': {
            fillOpacity: active ? 1 : 0.5,
          },
        },
        color: active ? 'white' : 'white50',
        fontFamily: 'IBM Plex Sans',
        flexShrink: 0,
      }}
    >
      {icon && <Svg svg={icon} width="2.4rem" height="2.4rem" />}
      <Text variant="paragraphBigBold" sx={{ color: active ? 'white' : 'white50' }}>
        {title}
      </Text>
      {active && <FeatureListItemUnderline />}
    </Button>
  )
}

export function FeatureChangerWrapper({ children }: { children: ReactNode }): ReactElement {
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        overflowX: 'hidden',
      }}
    >
      <Flex
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: ['2.4rem', '4rem'],
          justifyContent: 'flex-start',
          paddingBottom: '2rem',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '::-webkit-scrollbar': {
            display: 'none',
          },
          scrollBehavior: 'smooth',
          width: 'fit-content',
          maxWidth: '100%',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Flex>
      <FeatureDivider />
    </Flex>
  )
}
